import { Check, Circle, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/shadcn/utils';
import { useWorkspacePlan } from '@/modules/billing/hooks/useWorkspacePlan';
import { lacksFeature } from '@/modules/billing/workspacePlan';
import { Button } from '@/modules/ui/components/button/Button';
import { useIsLinkedInConnected } from '@/modules/user-external-accounts/hooks/useIsLinkedInConnected';
import { useIsSlackConnected } from '@/modules/user-external-accounts/hooks/useIsSlackConnected';
import { useUserExternalAccountsQuery } from '@/modules/user-external-accounts/queries/useUserExternalAccountsQuery';

interface OnboardingChecklistProps {
  /** Tenant selection carried on every portal link. */
  searchSuffix: string;
}

/**
 * The portal's first-run setup list, rendered in place of the dashboard
 * sections while the account has no jobs at all. Mounted only in that case, so
 * the Slack lookup it needs never costs a request on a working dashboard.
 */
export function OnboardingChecklist({
  searchSuffix,
}: OnboardingChecklistProps) {
  // Both hooks hide query state on purpose and read the same cache entry, so
  // the third call adds no request and only answers "has it answered yet".
  const { userId, isSlackConnected } = useIsSlackConnected();
  const { isLinkedInConnected } = useIsLinkedInConnected();
  const externalAccountsQuery = useUserExternalAccountsQuery(userId);
  // Only a successful lookup counts as known: a failed one would otherwise
  // report the accounts as disconnected and show a misleading "0 of 2 done".
  const isAccountsKnown = externalAccountsQuery.isSuccess;
  // Only self-service plans that include outreach have a reason to connect
  // LinkedIn. An unresolved plan keeps the row out until billing answers, so the list
  // never grows a row and restates its count a moment later.
  const workspacePlan = useWorkspacePlan();
  const showsLinkedIn =
    (workspacePlan.plan === 'source' || workspacePlan.plan === 'free') &&
    !lacksFeature(workspacePlan, 'outreach');

  // Creating a job is what makes this list appear, so it is never done here.
  const tasks = [
    {
      key: 'job',
      title: 'Create your first job',
      detail: 'Describe it to Finlay, then start sourcing candidates.',
      done: false,
      fromAccounts: false,
      action: { label: 'Create job', to: `/hm/chat${searchSuffix}` },
    },
    ...(showsLinkedIn
      ? [
          {
            key: 'linkedin',
            title: 'Connect LinkedIn',
            detail: 'Finlay reaches out to every match from your account.',
            done: isLinkedInConnected,
            fromAccounts: true,
            action: {
              label: 'Connect',
              to: `/hm/integrations${searchSuffix}`,
            },
          },
        ]
      : []),
    {
      key: 'slack',
      title: 'Connect Slack',
      detail:
        'Work with Finlay in Slack and get hiring updates as they happen.',
      done: isSlackConnected,
      fromAccounts: true,
      action: { label: 'Connect', to: `/hm/integrations${searchSuffix}` },
    },
  ];
  const doneCount = tasks.filter((task) => task.done).length;
  const nextTask = tasks.find((task) => !task.done);

  return (
    <section
      aria-labelledby="hm-setup-heading"
      className="flex flex-col gap-2.5"
    >
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex items-baseline gap-2">
          <h2
            className="text-[13px] leading-[18px] font-semibold"
            id="hm-setup-heading"
          >
            Get set up
          </h2>
          {isAccountsKnown && (
            <span className="text-[13px] font-medium text-neutral-500 tabular-nums">
              {doneCount} of {tasks.length} done
            </span>
          )}
        </div>
        {isAccountsKnown && (
          <span
            aria-hidden
            className="flex h-1.5 w-24 overflow-hidden rounded-full bg-neutral-200"
          >
            <span
              className="bg-semantic-560 rounded-full"
              style={{ width: `${(doneCount / tasks.length) * 100}%` }}
            />
          </span>
        )}
      </div>

      <ol className="divide-border divide-y border-y">
        {tasks.map((task) => {
          // An account row waits for its answer rather than flashing "Connect".
          const isPendingAnswer = task.fromAccounts && !isAccountsKnown;
          const isNext = task === nextTask && !isPendingAnswer;

          return (
            <li
              className={cn(
                'grid min-h-14 grid-cols-[24px_minmax(0,1fr)_auto] items-center gap-3 px-2 py-2.5',
                isNext && 'bg-primary/5 -mx-2 rounded-md px-4',
              )}
              key={task.key}
            >
              {task.done ? (
                <span className="bg-semantic-560 grid size-5 place-items-center rounded-full text-white">
                  <Check aria-hidden className="size-3" strokeWidth={3} />
                </span>
              ) : (
                <Circle
                  aria-hidden
                  className={cn(
                    'size-5',
                    isNext ? 'text-primary' : 'text-neutral-300',
                  )}
                  strokeWidth={1.5}
                />
              )}

              <div className="flex min-w-0 flex-col">
                <span
                  className={cn(
                    'truncate text-[13px] font-medium',
                    task.done &&
                      'text-neutral-500 line-through decoration-neutral-300',
                  )}
                >
                  {task.title}
                </span>
                <span className="text-muted-foreground text-[13px]">
                  {task.detail}
                </span>
              </div>

              {isPendingAnswer ? (
                <span />
              ) : task.done ? (
                <Link
                  className="text-xs font-medium text-neutral-500 hover:underline"
                  to={task.action.to}
                >
                  Manage
                </Link>
              ) : (
                <Button
                  asChild
                  size="sm"
                  variant={isNext ? 'primary' : 'outline'}
                >
                  <Link to={task.action.to}>
                    {isNext && <Plus />}
                    {task.action.label}
                  </Link>
                </Button>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
